import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { Ticket, Zap, Clock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export const UserCouponBanner = () => {
  const { user, session } = useAuth();
  const [activeCoupon, setActiveCoupon] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState("");
  const [isFlashing, setIsFlashing] = useState(false);

  const fetchCoupon = useCallback(async () => {
    // Use the auth user id — this is what the modal saves now
    const authUserId = user?.id ?? session?.user?.id;
    if (!authUserId) return;

    // Use a service-level read of site_settings (public read RLS)
    const { data, error } = await supabase
      .from("site_settings")
      .select("value")
      .eq("key", "global_user_coupons")
      .single();

    if (error) {
      console.warn("[UserCouponBanner] Error fetching coupons:", error.message);
      return;
    }

    if (data?.value) {
      try {
        const raw = typeof data.value === "string" ? data.value : JSON.stringify(data.value);
        const coupons: any[] = JSON.parse(raw);

        // Find the first non-expired coupon for this user
        const now = Date.now();
        const myCoupon = coupons.find(
          (c) =>
            c.user_id === authUserId &&
            c.expires_at &&
            new Date(c.expires_at).getTime() > now
        );

        setActiveCoupon(myCoupon ?? null);
      } catch (e) {
        console.warn("[UserCouponBanner] Error parsing coupons:", e);
        setActiveCoupon(null);
      }
    } else {
      setActiveCoupon(null);
    }
  }, [user?.id, session?.user?.id]);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchCoupon();
  }, [fetchCoupon]);

  // Poll every 30s as a safety net (in case realtime subscription drops)
  useEffect(() => {
    const timer = setInterval(fetchCoupon, 30_000);
    return () => clearInterval(timer);
  }, [fetchCoupon]);

  // Realtime: re-fetch when admin changes the site_settings row
  useEffect(() => {
    const authUserId = user?.id ?? session?.user?.id;
    if (!authUserId) return;

    const channel = supabase
      .channel(`coupon-realtime-${authUserId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "site_settings",
          filter: "key=eq.global_user_coupons",
        },
        () => {
          fetchCoupon();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, session?.user?.id, fetchCoupon]);

  // Countdown ticker
  useEffect(() => {
    if (!activeCoupon) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const distance = new Date(activeCoupon.expires_at).getTime() - now;

      if (distance <= 0) {
        setActiveCoupon(null);
        clearInterval(interval);
        return;
      }

      setIsFlashing(distance < 1_000 * 60 * 60); // flash when < 1 hour left

      const d = Math.floor(distance / (1_000 * 60 * 60 * 24));
      const h = Math.floor((distance % (1_000 * 60 * 60 * 24)) / (1_000 * 60 * 60));
      const m = Math.floor((distance % (1_000 * 60 * 60)) / (1_000 * 60));
      const s = Math.floor((distance % (1_000 * 60)) / 1_000);

      setTimeLeft(
        d > 0
          ? `${d}d ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
          : `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      );
    }, 1_000);

    return () => clearInterval(interval);
  }, [activeCoupon]);

  if (!activeCoupon) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="coupon-banner"
        initial={{ y: -90, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -90, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="fixed top-12 md:top-16 left-0 w-full z-30 px-2 pt-2 pointer-events-none"
      >
        <div className="max-w-lg mx-auto pointer-events-auto">
          <motion.div
            animate={
              isFlashing
                ? { boxShadow: ["0 0 8px rgba(239,68,68,0.4)", "0 0 30px rgba(239,68,68,0.9)", "0 0 8px rgba(239,68,68,0.4)"] }
                : { boxShadow: ["0 0 8px rgba(34,197,94,0.3)", "0 0 22px rgba(34,197,94,0.7)", "0 0 8px rgba(34,197,94,0.3)"] }
            }
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="bg-black/95 backdrop-blur-xl border border-green-500/50 rounded-2xl p-3 flex items-center justify-between gap-3 relative overflow-hidden"
          >
            {/* CRT scanline */}
            <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(0,255,0,0.02)_50%)] bg-[length:100%_4px] pointer-events-none" />

            {/* Left: icon + info */}
            <div className="flex items-center gap-3 relative z-10 flex-1 min-w-0">
              <motion.div
                animate={{ rotate: [0, -12, 12, -6, 6, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 4 }}
                className={`w-10 h-10 rounded-xl flex items-center justify-center border shrink-0 ${
                  isFlashing ? "bg-red-500/20 border-red-500/50" : "bg-green-500/20 border-green-500/50"
                }`}
              >
                <Ticket className={`w-5 h-5 ${isFlashing ? "text-red-400 animate-pulse" : "text-green-400"}`} />
              </motion.div>

              <div className="min-w-0">
                <p className="text-[9px] uppercase font-black tracking-widest text-green-500/70 flex items-center gap-1 mb-0.5">
                  <Zap className="w-2.5 h-2.5 animate-pulse" /> Sua Recompensa Exclusiva
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-lg font-display font-black text-white uppercase tracking-widest leading-none">
                    {activeCoupon.coupon_code}
                  </span>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase leading-tight shrink-0 ${
                    isFlashing ? "bg-red-500 text-white animate-pulse" : "bg-green-500 text-black"
                  }`}>
                    {activeCoupon.discount}% OFF
                  </span>
                </div>
                {/* Condicoes Especificas */}
                {(activeCoupon.min_photos || activeCoupon.max_photos || activeCoupon.limit_per_user === 1) && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {activeCoupon.min_photos > 0 && <span className={`text-[8.5px] border px-1.5 py-0.5 rounded uppercase font-black tracking-wider ${isFlashing ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-green-500/10 border-green-500/30 text-green-400"}`}>M{'\u00ED'}n {activeCoupon.min_photos} fotos</span>}
                    {activeCoupon.max_photos > 0 && <span className={`text-[8.5px] border px-1.5 py-0.5 rounded uppercase font-black tracking-wider ${isFlashing ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-green-500/10 border-green-500/30 text-green-400"}`}>M{'\u00E1'}x {activeCoupon.max_photos} fotos</span>}
                    {activeCoupon.limit_per_user === 1 && <span className={`text-[8.5px] border px-1.5 py-0.5 rounded uppercase font-black tracking-wider ${isFlashing ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-green-500/10 border-green-500/30 text-green-400"}`}>Uso {'\u00DA'}nico</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Right: countdown */}
            <div className="flex flex-col items-end relative z-10 shrink-0">
              <p className="text-[8px] text-white/40 uppercase tracking-widest font-bold mb-0.5">Expira em</p>
              <motion.div
                animate={isFlashing ? { scale: [1, 1.06, 1] } : {}}
                transition={{ duration: 0.4, repeat: Infinity }}
                className={`font-mono font-black text-xl tracking-widest leading-none flex items-center gap-1.5 ${
                  isFlashing ? "text-red-400" : "text-green-400"
                }`}
              >
                <Clock
                  className={`w-4 h-4 ${isFlashing ? "animate-spin" : ""}`}
                  style={{ animationDuration: "2s" }}
                />
                {timeLeft}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
